import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Drawer, Box, Stack, Typography, IconButton, Divider, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { commandCenterApi } from 'src/utils/commandCenterApi';
import formatMoney from 'src/utils/formatMoney';
import { getStageDisplayName } from 'src/utils/stageDisplay';

// Shared right-hand drill-through drawer. Pattern: PerformanceScorecard.js:582.
// Every aggregate on the page clicks through here; the header count == the drill
// total so the user sees drill count reconcile with the clicked aggregate (SC-002).
export default function DrillDrawer({ open, onClose, title, params }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (!open || !params) return undefined;
    let cancelled = false;
    setLoading(true);
    setRows([]);
    commandCenterApi
      .getDeals(params)
      .then((res) => {
        if (cancelled) return;
        setRows(res?.data?.data || []);
        setTotal(res?.data?.total || 0);
      })
      .catch(() => {
        if (cancelled) return;
        setRows([]);
        setTotal(0);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, params]);

  const ownerName = (o) => (o ? `${o.first_name || ''} ${o.last_name || ''}`.trim() || '—' : '—');

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 640 } } }}
    >
      <Box sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Box>
            <Typography variant="h6" fontWeight={700}>{title || 'Deals'}</Typography>
            <Typography variant="body2" color="text.secondary">
              {loading ? 'Loading…' : `${total} deal${total === 1 ? '' : 's'}`}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small"><Close /></IconButton>
        </Stack>
        <Divider sx={{ mb: 1 }} />

        {loading && <LinearProgress sx={{ mb: 1 }} />}

        <TableContainer sx={{ maxHeight: 'calc(100vh - 120px)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Company</TableCell>
                <TableCell>Stage</TableCell>
                <TableCell align="right">TCV</TableCell>
                <TableCell align="right">ARR</TableCell>
                <TableCell>BM</TableCell>
                <TableCell>BA</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  onClick={() => navigate(`/companies/${row.id}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{row.company_name}</Typography>
                  </TableCell>
                  <TableCell>
                    {row.stage_label ? (
                      <Chip label={getStageDisplayName(row.stage_label)} size="small" variant="outlined" />
                    ) : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {formatMoney(row.total_contract_value, row.currency?.label)}
                  </TableCell>
                  <TableCell align="right">
                    {formatMoney(row.arr, row.currency?.label)}
                  </TableCell>
                  <TableCell>{ownerName(row.bm)}</TableCell>
                  <TableCell>{ownerName(row.ba)}</TableCell>
                </TableRow>
              ))}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No deals for this selection.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Drawer>
  );
}
